import { BlueConicFilterType, ConditionOperatorType, FilterType, ProductsFilters } from './enums'

type BaseFilter = {
    blueConicType: BlueConicFilterType
    filterType: FilterType
}

export type AllFilterRecordMap = {
    included: FilterMap
    excluded: FilterMap
    products: ProductsFilters[]
}

export type Filter =
    | RelativeDateFilter
    | SpecificDateFilter
    | FreeTextFilter
    | MatchesAnyOfFilter
    | MatchesAllOfFilter
    | CustomFilter

export type RelativeDateFilter = BaseFilter & {
    blueConicType: BlueConicFilterType.PUBLICATION_DATE
    filterType: FilterType.RELATIVE_DATE
    value: {
        operator: ConditionOperatorType.LTE | ConditionOperatorType.GT
        value: number
        meta: Record<string, string> & { qualifier: 'days' | 'months' | 'weeks' }
    }
}

export type SpecificDateFilter = BaseFilter & {
    blueConicType: BlueConicFilterType.PUBLICATION_DATE
    filterType: FilterType.SPECIFIC_DATE
    value: {
        operator:
            | ConditionOperatorType.EQUALS
            | ConditionOperatorType.NOT_EQUALS
            | ConditionOperatorType.GT
            | ConditionOperatorType.GTE
            | ConditionOperatorType.LT
            | ConditionOperatorType.LTE
        value: Date | string
        meta: Record<string, string>
    }
    // | { [ConditionOperatorType.EQUALS]: Date | string; meta: Record<string, string> }
    // | { [ConditionOperatorType.NOT_EQUALS]: Date | string; meta: Record<string, string> }
    // | { [ConditionOperatorType.GT]: Date | string; meta: Record<string, string> }
    // | { [ConditionOperatorType.GTE]: Date | string; meta: Record<string, string> }
    // | { [ConditionOperatorType.LT]: Date | string; meta: Record<string, string> }
    // | { [ConditionOperatorType.LTE]: Date | string; meta: Record<string, string> }
}

export type FreeTextFilter = BaseFilter & {
    blueConicType: BlueConicFilterType.CUSTOM | BlueConicFilterType.PROPERTY
    filterType: FilterType.FREE_TEXT
    value?: string
}

export type MatchesAnyOfFilter = BaseFilter & {
    blueConicType: BlueConicFilterType.CATEGORY | BlueConicFilterType.KEYWORD | BlueConicFilterType.AUTHOR | BlueConicFilterType.SITENAME
    filterType: FilterType.MATCHES_ANY_OF
    value?: string[]
}

export type MatchesAllOfFilter = BaseFilter & {
    blueConicType: BlueConicFilterType.CATEGORY | BlueConicFilterType.KEYWORD | BlueConicFilterType.AUTHOR | BlueConicFilterType.SITENAME
    filterType: FilterType.MATCHES_ALL_OF
    value?: string[]
}

export type CustomFilter = BaseFilter & {
    blueConicType: BlueConicFilterType.CUSTOM
    filterType: FilterType.CUSTOM
    property: string
    nestedFilter:
        | Omit<FreeTextFilter, 'blueConicType'>
        | Omit<MatchesAnyOfFilter, 'blueConicType'>
        | Omit<MatchesAllOfFilter, 'blueConicType'>
        | Omit<SpecificDateFilter, 'blueConicType'>
        // | Omit<RelativeDateFilter, 'blueConicType'> // not available v1
        | NumericalFilter
}

export type MultiComparator = {
    operator: ConditionOperatorType.EQ_ANY
    value: number[]
}

export type SingleComparator = {
    operator:
        | ConditionOperatorType.LTE
        | ConditionOperatorType.LT
        | ConditionOperatorType.GTE
        | ConditionOperatorType.GT
    value: number
}
export type NumericalFilter = {
    filterType: FilterType.NUMERICAL
    value: SingleComparator // | MultiComparator should we support multi?
}

export const customFilterBlueConicType: string[] = [BlueConicFilterType.CUSTOM]
export const dateFilterBlueConicType: string[] = [BlueConicFilterType.PUBLICATION_DATE]
export const freeTextFilterBlueConicType: string[] = [BlueConicFilterType.PROPERTY]
export const anyOfOrAllOfFilterBlueConicType: string[] = [
    BlueConicFilterType.CATEGORY,
    BlueConicFilterType.KEYWORD,
    BlueConicFilterType.AUTHOR,
    BlueConicFilterType.SITENAME,
]

export function isCustomBlueConicType(v: any): v is BlueConicFilterType.CUSTOM {
    return customFilterBlueConicType.includes(v)
}

export function isFreeTextBlueConicType(v: any): v is BlueConicFilterType.PROPERTY {
    return freeTextFilterBlueConicType.includes(v)
}

export function isDateTypeBlueConicType(v: any): v is BlueConicFilterType.PUBLICATION_DATE {
    return dateFilterBlueConicType.includes(v)
}

export function filterIsFreeText(filter: any): filter is FreeTextFilter {
    return filter.filterType === FilterType.FREE_TEXT
}

export function filterIsAnyOfOrAllOf(filter: any): filter is MatchesAnyOfFilter | MatchesAllOfFilter {
    return [FilterType.MATCHES_ANY_OF, FilterType.MATCHES_ALL_OF].includes(filter.filterType)
}

export function filterIsDateType(filter: any): filter is RelativeDateFilter | SpecificDateFilter {
    return [FilterType.RELATIVE_DATE, FilterType.SPECIFIC_DATE].includes(filter.filterType)
}

export function filterIsSpecificDate(filter: any): filter is SpecificDateFilter {
    return filter.filterType === FilterType.SPECIFIC_DATE
}

export function filterIsCustom(filter: any): filter is CustomFilter {
    return filter.filterType === FilterType.CUSTOM
}

export function filterIsNumerical(filter: any): filter is NumericalFilter {
    return filter.filterType === FilterType.NUMERICAL
}

export function isAnyOrAllOfFilterBlueConicType(
    v: any,
): v is BlueConicFilterType.CATEGORY | BlueConicFilterType.KEYWORD | BlueConicFilterType.AUTHOR | BlueConicFilterType.SITENAME {
    return anyOfOrAllOfFilterBlueConicType.includes(v)
}

export type FilterMap = Map<string, Filter>

export type BlueConicPQueryDocument = {
    and: BlueConicPQueryRule[]
}

export type BlueConicPQueryRule =
    | { product_filter?: PropertyConditionValuePair }
    | { sitename?: PropertyConditionValuePair }
    | { publication_date?: PropertyConditionValuePair }
    | { category?: PropertyConditionValuePair }
    | { articlekeywords?: PropertyConditionValuePair }
    | { author?: PropertyConditionValuePair }

export type PropertyConditionValuePair =
    | (Record<ConditionOperatorType.EQUALS, string | number> & { meta?: Record<string, any> })
    | (Record<ConditionOperatorType.NOT_EQUALS, string | number> & { meta?: Record<string, any> })
    | (Record<ConditionOperatorType.GT, string | number> & { meta?: Record<string, any> })
    | (Record<ConditionOperatorType.GTE, string | number> & { meta?: Record<string, any> })
    | (Record<ConditionOperatorType.LT, string | number> & { meta?: Record<string, any> })
    | (Record<ConditionOperatorType.LTE, string | number> & { meta?: Record<string, any> })
    | (Record<ConditionOperatorType.EQ_ANY, string[]> & { meta?: Record<string, any> })
    | (Record<ConditionOperatorType.NEQ_ANY, string[]> & { meta?: Record<string, any> })
